import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export const PARTITION_OPERATOR_OUTPUT_A_ATTRIBUTE_SET = "PartitionOperator.aAttributeSet"
export const PARTITION_OPERATOR_OUTPUT_B_ATTRIBUTE_SET = "PartitionOperator.bAttributeSet"

export type PartitionOperatorParameters<TAttribute, TAttributeSet> = {
    name: string
    primaryAttribute: TAttribute | null
    attributeSet: TAttributeSet | null
}

export const partitionOperatorParametersSchema = <TAttribute, TAttributeSet>(
    attributeSchema: z.ZodMiniType<TAttribute, TAttribute>,
    attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>,
) =>
    z.object({
        name: z.string(),
        primaryAttribute: z.nullable(attributeSchema),
        attributeSet: z.nullable(attributeSetSchema),
    })

function applyPartitionOperator<T extends IndexingDim>(
    primaryAttribute: GeometryAttributeData<T>,
    attributeSet: GeometryAttributeSetData<T>,
): GeometryAttributeSetData<T> {
    const primaryAttrRefData = primaryAttribute.ref
    const attrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSet)

    const partitionedAttrRefDataSet = applyGeometryOperator(
        (primaryAttrRef: AttributeRef, attrRefSet: AttributeRefSet) => {
            const [aAttrRefSet, bPartAttrRefSet] = Operators.partition(primaryAttrRef, attrRefSet)
            return {
                aAttrRefSet,
                bPartAttrRefSet,
            }
        },
        primaryAttrRefData,
        attrRefDataSet,
    )

    const dim = primaryAttribute.dim
    const aAttributeSetData = geometryAttributeRefDataSetToGeometryAttributeSetData(partitionedAttrRefDataSet.aAttrRefSet, dim)
    const bAttributeSetData = geometryAttributeRefDataSetToGeometryAttributeSetData(partitionedAttrRefDataSet.bPartAttrRefSet, dim)

    return createGeometryAttributeSetData(
        {
            [`${PARTITION_OPERATOR_OUTPUT_A_ATTRIBUTE_SET}`]: aAttributeSetData,
            [`${PARTITION_OPERATOR_OUTPUT_B_ATTRIBUTE_SET}`]: bAttributeSetData,
        },
        dim,
    )
}

export abstract class GeometryPartitionOperatorNode<
    out T extends IndexingDim = IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null> = EvaluableTemplateNode<GeometryAttributeData<T> | null>,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null> = EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, PartitionOperatorParameters<TAttribute, TAttributeSet>> {
    protected override hasValidParameters(
        parameters: PartitionOperatorParameters<TAttribute, TAttributeSet>,
    ): parameters is PartitionOperatorParameters<TAttribute, TAttributeSet> & NonNullableParameters<PartitionOperatorParameters<TAttribute, TAttributeSet>> {
        return parameters.primaryAttribute !== null && parameters.attributeSet !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<PartitionOperatorParameters<TAttribute, TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {primaryAttribute, attributeSet} = parameters

        const [primaryAttributeDataValid, primaryAttributeDataInvalid] = scope.branch(evaluator.evaluate(primaryAttribute))
        const [attributeSetDataValid, attributeSetDataInvalid] = scope.branch(evaluator.evaluate(attributeSet))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(primaryAttributeDataValid, attributeSetDataValid),
            ([primaryAttributeData, attributeSetData]) => applyPartitionOperator(primaryAttributeData, attributeSetData),
            "applyPartitionOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeData,
            invalidator: scope.phi(primaryAttributeDataInvalid, attributeSetDataInvalid),
        }
    }
}
